import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

/**
 * External-link confirmation banner — implementation 2026-04-27.
 *
 * Mounted once at the app root via <ExternalLinkProvider>. The provider:
 *   1. Renders a single modal in the DOM.
 *   2. Exposes useExternalLink().open(href, label?) for explicit triggers.
 *   3. Installs a capture-phase document click listener that automatically
 *      intercepts clicks on any <a> whose href points to a non-Anderson
 *      domain — no per-link wiring required.
 *
 * UX:
 *   • Backdrop blur, centered card, brand red+blue gradient accent strip.
 *   • 5-second countdown ring (SVG, animated stroke-dashoffset).
 *   • [Stay on Anderson] cancels, [Continue] proceeds immediately.
 *   • Esc key cancels. Backdrop click cancels. Click on banner does not.
 *   • Opens the destination in a new tab with rel="noopener noreferrer".
 *   • Skips known internal hosts: anderson*.com / .ca / .preview.emergentagent.com.
 */

const COUNTDOWN_SECONDS = 5;
const RING_LEN = 119.38; // 2π × 19

const Ctx = createContext({ open: () => {} });

// Hosts considered "internal" — clicks to these never trigger the banner.
const INTERNAL_HOST_PATTERNS = [
  /(^|\.)andersoncollege\.com$/i,
  /(^|\.)andersoncollege\.ca$/i,
  /\.preview\.emergentagent\.com$/i,
  /(^|\.)staging21\.andersoncollege\.com$/i,
  /(^|\.)viewbook\.andersoncollege\.com$/i,
];

const isInternalHost = (host) => {
  if (!host) return true;
  if (typeof window !== "undefined" && host === window.location.host) return true;
  return INTERNAL_HOST_PATTERNS.some((re) => re.test(host));
};

export const useExternalLink = () => useContext(Ctx);

export const ExternalLinkProvider = ({ children }) => {
  const [state, setState] = useState({
    isOpen: false,
    href: "",
    host: "",
    label: "",
  });
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const timerRef = useRef(null);
  const proceedBtnRef = useRef(null);

  const close = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setState((s) => ({ ...s, isOpen: false }));
  }, []);

  const proceed = useCallback(() => {
    const url = state.href;
    if (!url) {
      close();
      return;
    }
    // Open in a new tab so users can come back to Anderson easily.
    window.open(url, "_blank", "noopener,noreferrer");
    close();
  }, [state.href, close]);

  const open = useCallback((href, label = "") => {
    if (!href) return;
    let host = "";
    try {
      host = new URL(href, window.location.href).host;
    } catch (_) {
      // Invalid URL — just navigate normally.
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    if (isInternalHost(host)) {
      // Internal — open without confirmation.
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    setState({ isOpen: true, href, host, label });
    setSecondsLeft(COUNTDOWN_SECONDS);
  }, []);

  // Auto-intercept clicks on any external <a>
  useEffect(() => {
    const handler = (e) => {
      // Only normal left-clicks without modifier keys
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (
        !href ||
        href.startsWith("#") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:") ||
        href.startsWith("javascript:")
      ) return;
      // Allow opt-out via data-skip-external-banner="true"
      if (a.dataset.skipExternalBanner === "true") return;
      let host = "";
      try {
        host = new URL(href, window.location.href).host;
      } catch (_) {
        return;
      }
      if (isInternalHost(host)) return;
      e.preventDefault();
      e.stopPropagation();
      const label =
        a.dataset.externalLabel ||
        (a.textContent || "").trim().replace(/\s+/g, " ").slice(0, 80) ||
        host;
      open(href, label);
    };
    document.addEventListener("click", handler, true); // capture phase
    return () => document.removeEventListener("click", handler, true);
  }, [open]);

  // Countdown
  useEffect(() => {
    if (!state.isOpen) return undefined;
    setSecondsLeft(COUNTDOWN_SECONDS);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          // proceed via timeout so React state updates before navigation
          setTimeout(() => proceed(), 0);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [state.isOpen, proceed]);

  // Escape closes; focus the proceed button on open
  useEffect(() => {
    if (!state.isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    // focus next tick so Safari doesn't fight the transition
    const t = setTimeout(() => proceedBtnRef.current?.focus(), 60);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(t);
    };
  }, [state.isOpen, close]);

  // Drain ring from 0 → RING_LEN over COUNTDOWN_SECONDS
  const ringOffset = state.isOpen
    ? (RING_LEN * (COUNTDOWN_SECONDS - secondsLeft)) / COUNTDOWN_SECONDS
    : 0;

  return (
    <Ctx.Provider value={{ open }}>
      {children}
      <div
        className={`ext-overlay ${state.isOpen ? "is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ext-banner-title"
        aria-describedby="ext-banner-msg"
        onClick={(e) => {
          if (e.target === e.currentTarget) close();
        }}
        data-testid="external-link-banner"
      >
        <div className="ext-banner">
          <div className="ext-accent" />

          <div className="ext-countdown" aria-hidden="true">
            <svg viewBox="0 0 44 44">
              <circle className="track" cx="22" cy="22" r="19" />
              <circle
                className="progress"
                cx="22"
                cy="22"
                r="19"
                style={{
                  strokeDasharray: RING_LEN,
                  strokeDashoffset: ringOffset,
                  transition: state.isOpen
                    ? "stroke-dashoffset 1s linear"
                    : "none",
                }}
              />
            </svg>
            <div className="ext-countdown-label" data-testid="ext-banner-countdown">
              {secondsLeft}
            </div>
          </div>

          <div className="ext-body">
            <span className="ext-eyebrow">
              <span aria-hidden="true">↗</span> Leaving Anderson College
            </span>
            <h2 className="ext-title" id="ext-banner-title">
              You're heading to a third-party site.
            </h2>
            {state.host && (
              <span className="ext-domain" data-testid="ext-banner-host">
                {state.host}
              </span>
            )}
            <p className="ext-msg" id="ext-banner-msg">
              We're sending you to{" "}
              <strong>{state.label || state.host}</strong>. Anderson College isn't
              responsible for its content or privacy practices. You can come back
              anytime.
            </p>

            <div className="ext-actions">
              <button
                type="button"
                ref={proceedBtnRef}
                onClick={proceed}
                className="ext-btn ext-btn-primary"
                data-testid="ext-banner-continue"
              >
                Continue
              </button>
              <button
                type="button"
                onClick={close}
                className="ext-btn ext-btn-secondary"
                data-testid="ext-banner-cancel"
              >
                Stay on Anderson
              </button>
            </div>

            <p className="ext-foot">
              Auto-continues in {secondsLeft}s · Press <kbd>Esc</kbd> to cancel
            </p>
          </div>
        </div>
      </div>
    </Ctx.Provider>
  );
};

export default ExternalLinkProvider;
